import {AssetPartDimensions, CustomPreviewProps, DriveLayout} from '@domain/kiln-monitoring'

import {Bearing, BoxPart, GirthGear, LocationPill, Pinion} from './AtomicParts'
import {OpenDriveParts, DriveParts, PinionParts} from './KilnParts.types'
import {buildDriveLocations} from './KilnParts.utils'

type DrivePartProps = Omit<DriveLayout, 'dimensions'> & {
  dimensions?: AssetPartDimensions
  customSvgPreview?: (props: CustomPreviewProps) => React.ReactNode
  widthScaleFactor?: number
  meterToPixel?: number
}

export const DrivePart: React.FC<DrivePartProps> = ({
  id,
  x,
  y,
  width,
  height,
  position,
  showLocation = false,
  flip = false,
  customSvgPreview,
  meterToPixel,
  dimensions
}) => {
  const yOffset = position === 'bottom' ? 0 : 60

  const xPosition =
    dimensions?.distanceFrom0 && meterToPixel ? dimensions.distanceFrom0 * meterToPixel : x
  const driveWidth = dimensions?.width && meterToPixel ? dimensions.width * meterToPixel : width

  return (
    <svg x={xPosition + driveWidth / 2} y={y} width={driveWidth} height={height}>
      {customSvgPreview ? (
        customSvgPreview({
          x: 0,
          y: 20,
          width: driveWidth,
          height: 168
        })
      ) : (
        <g transform={flip ? `scale(-1, 1) translate(${-width}, 0)` : ''}>
          {/* TOP OR DUAL DRIVE */}
          {position !== 'bottom' && (
            <svg>
              <line x1={48} y1={59} x2={60} y2={59} stroke="#00274D" strokeWidth={2} />
              <line x1={23} y1={32} x2={23} y2={44} stroke="#00274D" strokeWidth={2} />
              <Bearing
                id={`${id}_${OpenDriveParts.DRIVE_TOP}_${DriveParts.PINION}_${PinionParts.BEARING_LEFT}`}
                y={52}
                x={60}
              />
              <Bearing
                id={`${id}_${OpenDriveParts.DRIVE_TOP}_${DriveParts.PINION}_${PinionParts.BEARING_RIGHT}`}
                y={52}
                x={112}
              />
              <Pinion
                id={`${id}_${OpenDriveParts.DRIVE_TOP}_${DriveParts.PINION}_${PinionParts.PINION}`}
                x={72}
                y={44}
              />
              <BoxPart
                id={`${id}_${OpenDriveParts.DRIVE_TOP}_${DriveParts.GEARBOX}`}
                x={0}
                y={44}
              />
              <BoxPart id={`${id}_${OpenDriveParts.DRIVE_TOP}_${DriveParts.MOTOR}`} x={0} y={0} />
            </svg>
          )}
          {/* BOTTOM OR DUAL DRIVE */}
          {position !== 'top' && (
            <svg y={yOffset}>
              <line x1={48} y1={207} x2={60} y2={207} stroke="#00274D" strokeWidth={2} />
              <line x1={23} y1={224} x2={23} y2={236} stroke="#00274D" strokeWidth={2} />
              <Bearing
                id={`${id}_${OpenDriveParts.DRIVE_BOTTOM}_${DriveParts.PINION}_${PinionParts.BEARING_LEFT}`}
                y={200}
                x={60}
              />
              <Bearing
                id={`${id}_${OpenDriveParts.DRIVE_BOTTOM}_${DriveParts.PINION}_${PinionParts.BEARING_RIGHT}`}
                y={200}
                x={112}
              />
              <Pinion
                id={`${id}_${OpenDriveParts.DRIVE_BOTTOM}_${DriveParts.PINION}_${PinionParts.PINION}`}
                x={72}
                y={192}
              />
              <BoxPart
                id={`${id}_${OpenDriveParts.DRIVE_BOTTOM}_${DriveParts.GEARBOX}`}
                x={0}
                y={192}
              />
              <BoxPart
                id={`${id}_${OpenDriveParts.DRIVE_BOTTOM}_${DriveParts.MOTOR}`}
                x={0}
                y={236}
              />
            </svg>
          )}
          <GirthGear id={`${id}_${OpenDriveParts.GIRTH_GEAR}`} x={75} y={yOffset} />
          {showLocation && renderLocationPills({id, pillOffsetY: 0, position, flip: flip})}
        </g>
      )}
    </svg>
  )
}

const renderLocationPills = ({id, pillOffsetY, position, ...props}) => {
  const locationPills = buildDriveLocations({id, position})

  return locationPills.map((pill) => (
    <LocationPill
      key={pill.locationNumber}
      x={pill.x}
      y={pill.y + pillOffsetY}
      text={pill.locationNumber}
      {...props}
    />
  ))
}
